import React, { useState, useCallback, useMemo, useEffect } from "react";
import restService from "../../../services/restService";
import {
  FilterOverlay,
  FilterModal,
  FilterHeader,
  FilterTitle,
  FilterIcon,
  ActiveFiltersBadge,
  CloseButton,
  FilterContent,
  FilterSection,
  SectionHeader,
  SectionIcon,
  SectionTitle,
  ModernDateModeSelector,
  DateModeButton,
  InputRow,
  ModernInputGroup,
  InputLabel,
  ModernInput,
  ModernSelect,
  AmountInputWrapper,
  CurrencyPrefix,
  ErrorMessage,
  FilterFooter,
  ActionButtons,
  ClearButton,
  CancelButton,
  ApplyButton,
  LoadingSpinner
} from "./TransactionFilterModal.styles.jsx";

const TransactionFilterModal = ({ visible, onApplyFilters, onCancel, loading = false, initialFilters = {} }) => {
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    date: "",
    status: "",
    send_amount: "",
    receive_amount: "",
    source_currency: "",
    destination_currency: "",
    ...initialFilters
  });

  const [dateMode, setDateMode] = useState(() => {
    // Determine initial date mode based on existing filters
    if (initialFilters.date) return "specific";
    if (initialFilters.start_date || initialFilters.end_date) return "range";
    return "range";
  });
  const [errors, setErrors] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);

  // Fetch currencies from countries API
  useEffect(() => {
    const fetchCurrencies = async () => {
      setLoadingCurrencies(true);
      try {
        const response = await restService.UtilsApi.getCountries();
        if (response?.data?.data) {
          // Extract unique currency codes from countries
          const uniqueCurrencies = [...new Set(
            response.data.data
              .map(country => country.currency_code)
              .filter(code => code)
          )].sort();
          
          setCurrencies(uniqueCurrencies);
        }
      } catch (error) {
        console.error("Failed to fetch currencies:", error);
        // Fallback to common currencies if API fails
        setCurrencies(['USD', 'GHS', 'EUR', 'GBP', 'NGN', 'KES', 'UGX', 'TZS']);
      } finally {
        setLoadingCurrencies(false);
      }
    };

    fetchCurrencies();
  }, []);

  // Update filters when initialFilters prop changes
  useEffect(() => {
    if (visible) {
      setFilters({
        start_date: "",
        end_date: "",
        date: "",
        status: "",
        send_amount: "",
        receive_amount: "",
        source_currency: "",
        destination_currency: "",
        ...initialFilters
      });
      
      // Update date mode based on current filters
      if (initialFilters.date) {
        setDateMode("specific");
      } else if (initialFilters.start_date || initialFilters.end_date) {
        setDateMode("range");
      }
      
      // Clear any previous errors
      setErrors({});
    }
  }, [visible, initialFilters]);

  // Status options matching the API
  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "success", label: "Success" },
    { value: "accepted", label: "Accepted" },
    { value: "created", label: "Created" },
    { value: "error", label: "Error" },
    { value: "failed", label: "Failed" },
    { value: "reversed", label: "Reversed" },
    { value: "dispatched", label: "Dispatched" }
  ];


  // Currency options from API
  const currencyOptions = useMemo(() => {
    const options = [{ value: "", label: "All Currencies" }];
    
    currencies.forEach(currency => {
      options.push({
        value: currency,
        label: currency
      });
    });
    
    return options;
  }, [currencies]);

  const handleInputChange = useCallback((field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  }, [errors]);

  const validateFilters = useCallback(() => {
    const newErrors = {};

    // Validate date inputs
    if (dateMode === "range") {
      if (filters.start_date && filters.end_date) {
        const startDate = new Date(filters.start_date);
        const endDate = new Date(filters.end_date);
        if (startDate > endDate) {
          newErrors.date_range = "Start date must be before end date";
        }
      }
    }

    // Validate numeric inputs
    if (filters.send_amount && (isNaN(filters.send_amount) || parseFloat(filters.send_amount) < 0)) {
      newErrors.send_amount = "Send amount must be a positive number";
    }

    if (filters.receive_amount && (isNaN(filters.receive_amount) || parseFloat(filters.receive_amount) < 0)) {
      newErrors.receive_amount = "Receive amount must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [filters, dateMode]);

  const handleApplyFilters = useCallback(() => {
    if (!validateFilters()) {
      return;
    }

    // Build query parameters based on active filters
    const queryParams = {};
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.toString().trim()) {
        // Handle date mode logic
        if (key === "start_date" || key === "end_date") {
          if (dateMode === "range") {
            queryParams[key] = value;
          }
        } else if (key === "date") {
          if (dateMode === "specific") {
            queryParams[key] = value;
          }
        } else {
          queryParams[key] = value;
        }
      }
    });

    onApplyFilters(queryParams);
  }, [filters, dateMode, validateFilters, onApplyFilters]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      start_date: "",
      end_date: "",
      date: "",
      status: "",
      send_amount: "",
      receive_amount: "",
      source_currency: "",
      destination_currency: ""
    });
    setErrors({});
  }, []);

  const activeFiltersCount = useMemo(() => {
    return Object.values(filters).filter(value => value && value.toString().trim()).length;
  }, [filters]);

  if (!visible) return null;

  return (
    <FilterOverlay>
      <FilterModal>
        <FilterHeader>
          <FilterTitle>
            <FilterIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3"></polygon>
              </svg>
            </FilterIcon>
            <h2>Filter Transactions</h2>
            {activeFiltersCount > 0 && (
              <ActiveFiltersBadge>{activeFiltersCount}</ActiveFiltersBadge>
            )}
          </FilterTitle>
          <CloseButton onClick={onCancel} disabled={loading}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </CloseButton>
        </FilterHeader>

        <FilterContent>
          {/* Date Filtering Section */}
          <FilterSection>
            <SectionHeader>
              <SectionIcon>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </SectionIcon>
              <SectionTitle>Date Range</SectionTitle>
            </SectionHeader>
            
            <ModernDateModeSelector>
              <DateModeButton 
                $active={dateMode === "range"}
                onClick={() => setDateMode("range")}
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Date Range
              </DateModeButton>
              <DateModeButton 
                $active={dateMode === "specific"}
                onClick={() => setDateMode("specific")}
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12,6 12,12 16,14"></polyline>
                </svg>
                Specific Date
              </DateModeButton>
            </ModernDateModeSelector>

            {dateMode === "range" ? (
              <InputRow>
                <ModernInputGroup>
                  <InputLabel>From Date</InputLabel>
                  <ModernInput
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => handleInputChange("start_date", e.target.value)}
                    $hasError={!!errors.date_range}
                  />
                </ModernInputGroup>
                <ModernInputGroup>
                  <InputLabel>To Date</InputLabel>
                  <ModernInput
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => handleInputChange("end_date", e.target.value)}
                    $hasError={!!errors.date_range}
                  />
                </ModernInputGroup>
              </InputRow>
            ) : (
              <ModernInputGroup>
                <InputLabel>Select Date</InputLabel>
                <ModernInput
                  type="date"
                  value={filters.date}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                />
              </ModernInputGroup>
            )}
            {errors.date_range && <ErrorMessage>{errors.date_range}</ErrorMessage>}
          </FilterSection>

          {/* Transaction Status Section */}
          <FilterSection>
            <SectionHeader>
              <SectionIcon>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"></path>
                  <circle cx="12" cy="12" r="10"></circle>
                </svg>
              </SectionIcon>
              <SectionTitle>Transaction Status</SectionTitle>
            </SectionHeader>
            
            <ModernInputGroup>
              <InputLabel>Status</InputLabel>
              <ModernSelect
                value={filters.status}
                onChange={(e) => handleInputChange("status", e.target.value)}
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </ModernSelect>
            </ModernInputGroup>
          </FilterSection>

          {/* Currency & Amount Section */}
          <FilterSection>
            <SectionHeader>
              <SectionIcon>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
              </SectionIcon>
              <SectionTitle>Currency & Amount</SectionTitle>
            </SectionHeader>
            
            <InputRow>
              <ModernInputGroup>
                <InputLabel>Send Currency</InputLabel>
                <ModernSelect
                  value={filters.source_currency}
                  onChange={(e) => handleInputChange("source_currency", e.target.value)}
                  disabled={loadingCurrencies}
                >
                  {loadingCurrencies ? (
                    <option value="">Loading currencies...</option>
                  ) : (
                    currencyOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))
                  )}
                </ModernSelect>
              </ModernInputGroup>
              <ModernInputGroup>
                <InputLabel>Receive Currency</InputLabel>
                <ModernSelect
                  value={filters.destination_currency}
                  onChange={(e) => handleInputChange("destination_currency", e.target.value)}
                  disabled={loadingCurrencies}
                >
                  {loadingCurrencies ? (
                    <option value="">Loading currencies...</option>
                  ) : (
                    currencyOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))
                  )}
                </ModernSelect>
              </ModernInputGroup>
            </InputRow>
            
            <InputRow>
              <ModernInputGroup>
                <InputLabel>Send Amount</InputLabel>
                <AmountInputWrapper>
                  <ModernInput
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={filters.send_amount}
                    onChange={(e) => handleInputChange("send_amount", e.target.value)}
                    $hasError={!!errors.send_amount}
                    $hasPrefix={true}
                  />
                </AmountInputWrapper>
                {errors.send_amount && <ErrorMessage>{errors.send_amount}</ErrorMessage>}
              </ModernInputGroup>
              <ModernInputGroup>
                <InputLabel>Receive Amount</InputLabel>
                <AmountInputWrapper>
                  <ModernInput
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={filters.receive_amount}
                    onChange={(e) => handleInputChange("receive_amount", e.target.value)}
                    $hasError={!!errors.receive_amount}
                    $hasPrefix={true}
                  />
                </AmountInputWrapper>
                {errors.receive_amount && <ErrorMessage>{errors.receive_amount}</ErrorMessage>}
              </ModernInputGroup>
            </InputRow>
          </FilterSection>
        </FilterContent>

        <FilterFooter>
          <ClearButton onClick={handleClearFilters} disabled={loading}>
            Clear All
          </ClearButton>
          <ActionButtons>
            <CancelButton onClick={onCancel} disabled={loading}>
              Cancel
            </CancelButton>
            <ApplyButton onClick={handleApplyFilters} disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  Applying...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  Apply Filters
                </>
              )}
            </ApplyButton>
          </ActionButtons>
        </FilterFooter>
      </FilterModal>
    </FilterOverlay>
  );
};



export default TransactionFilterModal;